// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@eisinc/App/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import siteMessage from "@insite/client-framework/SiteMessage";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const { suggestions } = state.xAutoCompleteSearchSuggestions.querySuggestions;
    const {
        pages: {
            productList: { isLoading },
        },
    } = state;

    return {
        suggestions,
        isLoading,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
};

type Props = ReturnType<typeof mapStateToProps> & WidgetProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NotFoundSearchResultStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    wrapper?: InjectableCss;
    suggestion?: InjectableCss;
    notFoundText?: TypographyPresentationProps;
}

export const notFoundSearchResultStyles: NotFoundSearchResultStyles = {};

const styles = notFoundSearchResultStyles;

const NotFoundSearchResult = ({ history, location, suggestions, isLoading, getAutocompleteModel }: Props) => {
    const parsedQuery = parseQueryString<{ query?: string }>(location.search)?.query;
    const querySuggestionParams = {
        searchHub: "listing",
        tab: "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        locationHref: document?.location?.href,
        referringUrl: document?.referrer,
    };
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true);

    useEffect(() => {
        if (!suggestions && parsedQuery) {
            getAutocompleteModel({
                query: parsedQuery,
                isQuerySuggestionEnabled: true,
                ...querySuggestionParams,
                onComplete() {
                    setIsLoadingSuggestions(false);
                },
            } as any);
        } else {
            setIsLoadingSuggestions(false);
        }
    }, []);

    if (isLoading || isLoadingSuggestions) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.notFoundText}>
                {siteMessage("Custom_Not_Found_Search_Result", parsedQuery || "")}
            </Typography>

            {!!suggestions?.length && (
                <StyledWrapper {...styles.suggestion}>
                    <Typography>{siteMessage("Custom_Not_Found_Search_Suggestions", "Did you mean?")}</Typography>
                    <ul>
                        {suggestions.map(suggestionItem => (
                            <li key={suggestionItem.id}>
                                <Link
                                    onClick={() => history.push(`/Search?query=${suggestionItem?.suggestion}`)}
                                    data-test-selector="Search_Result_Suggestion_Link"
                                >
                                    {suggestionItem.suggestion}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(NotFoundSearchResult)),
    definition: {
        group: "Product List",
        displayName: "Not Found Search Result",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
